/**
 * Flow editor — local folder access via the File System Access API.
 *
 * Lets the (Chromium) web app read and write `.flow` maps directly inside a
 * folder the user picks on their own PC — no server round-trip and no download
 * dialog. The chosen directory handle is persisted in IndexedDB so it survives
 * reloads; the browser re-prompts for permission the first time it's used each
 * session (a single click).
 *
 * Unsupported browsers (Firefox / Safari, which lack `showDirectoryPicker`)
 * fall back to the existing server-save / upload / download flow —
 * `isFsAccessSupported()` returns false there so callers can branch.
 */

// ── Minimal typings ──────────────────────────────────────────────────────────
// The TS DOM lib doesn't yet ship `showDirectoryPicker` on Window nor the
// permission methods on the handles, so we declare just what we use.
type FsPermission = 'granted' | 'denied' | 'prompt';

interface FsWritable {
  write(data: Blob | BufferSource): Promise<void>;
  close(): Promise<void>;
}
interface FsFileHandle {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<FsWritable>;
}
interface FsDirHandle {
  kind: 'directory';
  name: string;
  values(): AsyncIterableIterator<FsFileHandle | FsDirHandle>;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FsFileHandle>;
  removeEntry(name: string, opts?: { recursive?: boolean }): Promise<void>;
  queryPermission(opts: { mode: 'read' | 'readwrite' }): Promise<FsPermission>;
  requestPermission(opts: { mode: 'read' | 'readwrite' }): Promise<FsPermission>;
}

const IDB_NAME = 'synapse-flow-fs';
const IDB_STORE = 'handles';
const HANDLE_KEY = 'dir';

/** The currently-connected folder handle (in memory for this session). */
let _dir: FsDirHandle | null = null;
const _listeners = new Set<() => void>();

/** True when the browser supports picking/reading/writing a local folder. */
export function isFsAccessSupported(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker
    === 'function';
}

function notify(): void {
  _listeners.forEach(l => { try { l(); } catch { /* ignore */ } });
}

/** Subscribe to connect/disconnect changes (returns an unsubscribe fn). */
export function subscribeFolder(fn: () => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

/** Name of the connected folder, or null when none is connected. */
export function connectedFolderName(): string | null {
  return _dir?.name ?? null;
}

// ── IndexedDB (persist the directory handle across reloads) ───────────────────
function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await openIdb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openIdb();
  try {
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const r = tx.objectStore(IDB_STORE).get(key);
      r.onsuccess = () => resolve((r.result as T) ?? null);
      r.onerror = () => reject(r.error);
    });
  } finally { db.close(); }
}

async function idbDelete(key: string): Promise<void> {
  const db = await openIdb();
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } finally { db.close(); }
}

/**
 * Restore a previously-connected folder handle from IndexedDB. Does NOT
 * request permission (that needs a user gesture) — it just re-attaches the
 * handle so the UI can show the folder name and re-grant on first use.
 * Returns the folder name or null.
 */
export async function restoreFolder(): Promise<string | null> {
  if (!isFsAccessSupported()) return null;
  try {
    const h = await idbGet<FsDirHandle>(HANDLE_KEY);
    if (h && h.kind === 'directory') {
      _dir = h;
      notify();
      return h.name;
    }
  } catch { /* ignore */ }
  return null;
}

/** Prompt the user to pick a folder. Persists the handle. Returns its name. */
export async function pickFolder(): Promise<string | null> {
  if (!isFsAccessSupported()) {
    throw new Error('This browser does not support local folders (use Chrome or Edge).');
  }
  const picker = (window as unknown as {
    showDirectoryPicker: (opts?: { mode?: string; id?: string }) => Promise<FsDirHandle>;
  }).showDirectoryPicker;
  const h = await picker({ mode: 'readwrite', id: 'synapse-flow' });
  _dir = h;
  try { await idbPut(HANDLE_KEY, h); } catch { /* ignore */ }
  notify();
  return h.name;
}

/** Forget the connected folder (clears memory + IndexedDB). */
export async function disconnectFolder(): Promise<void> {
  _dir = null;
  await idbDelete(HANDLE_KEY).catch(() => { /* ignore */ });
  notify();
}

/** Ensure read/write permission, requesting it if needed (needs a gesture). */
async function ensurePermission(mode: 'read' | 'readwrite'): Promise<boolean> {
  if (!_dir) return false;
  try {
    if ((await _dir.queryPermission({ mode })) === 'granted') return true;
    return (await _dir.requestPermission({ mode })) === 'granted';
  } catch { return false; }
}

export interface LocalChartEntry {
  name: string;
  /** Last-modified epoch ms, for sorting / display. */
  modified: number;
  size: number;
}

/** List `.flow` files in the connected folder (requests read permission). */
export async function listFolderCharts(): Promise<LocalChartEntry[]> {
  if (!_dir) return [];
  if (!(await ensurePermission('read'))) {
    throw new Error('Permission to read the folder was denied.');
  }
  const out: LocalChartEntry[] = [];
  for await (const entry of _dir.values()) {
    if (entry.kind === 'file' && /\.flow$/i.test(entry.name)) {
      try {
        const f = await (entry as FsFileHandle).getFile();
        out.push({ name: entry.name, modified: f.lastModified, size: f.size });
      } catch {
        out.push({ name: entry.name, modified: 0, size: 0 });
      }
    }
  }
  out.sort((a, b) => b.modified - a.modified);
  return out;
}

/** Read a `.flow` file from the connected folder as a File. */
export async function readFolderChart(name: string): Promise<File> {
  if (!_dir) throw new Error('No folder connected.');
  if (!(await ensurePermission('read'))) {
    throw new Error('Permission to read the folder was denied.');
  }
  const fh = await _dir.getFileHandle(name);
  return fh.getFile();
}

/** Write a `.flow` blob into the connected folder (overwrites in place). */
export async function writeFolderChart(name: string, blob: Blob): Promise<void> {
  if (!_dir) throw new Error('No folder connected.');
  if (!(await ensurePermission('readwrite'))) {
    throw new Error('Permission to write to the folder was denied.');
  }
  const fh = await _dir.getFileHandle(name, { create: true });
  const writable = await fh.createWritable();
  await writable.write(blob);
  await writable.close();
}

/** Delete a `.flow` file from the connected folder. */
export async function deleteFolderChart(name: string): Promise<void> {
  if (!_dir) throw new Error('No folder connected.');
  if (!(await ensurePermission('readwrite'))) {
    throw new Error('Permission to modify the folder was denied.');
  }
  await _dir.removeEntry(name);
}
